import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    Card,
    CardContent,
    Grid,
    TextField,
    MenuItem,
    Stack,
    Divider,
    Alert,
    CircularProgress,
    FormControlLabel,
    Switch
} from '@mui/material';
import { Save as SaveIcon, Send as SendIcon } from '@mui/icons-material';
import { useApiHandler } from '../../../hooks/useApiHandler';
import DashboardLayout from '../../../components/layouts/DashboardLayout';
import emailSettingService from '../../../services/emailSettingService';

const MAILERS = [
    { value: 'smtp', label: 'SMTP' },
    { value: 'sendmail', label: 'Sendmail' },
    { value: 'log', label: 'Log only (no mail sent)' }
];

const ENCRYPTIONS = [
    { value: 'tls', label: 'TLS' },
    { value: 'ssl', label: 'SSL' },
    { value: 'none', label: 'None' }
];

/**
 * SMTP configuration used for invoice and reminder emails.
 * The stored password is never sent back to the browser; leaving the field
 * blank keeps whatever is already saved.
 */
const EmailSettings = () => {
    const { showError, showSuccess } = useApiHandler();

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [hasPassword, setHasPassword] = useState(false);
    const [testEmail, setTestEmail] = useState('');
    const [errors, setErrors] = useState({});
    const [form, setForm] = useState({
        mailer: 'smtp',
        host: '',
        port: 587,
        username: '',
        password: '',
        encryption: 'tls',
        from_address: '',
        from_name: '',
        is_active: false
    });

    useEffect(() => {
        loadSettings();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadSettings = async () => {
        setLoading(true);
        const result = await emailSettingService.getSettings();

        if (result.success) {
            const data = result.data;
            setHasPassword(!!data.has_password);
            setForm({
                mailer: data.mailer || 'smtp',
                host: data.host || '',
                port: data.port || 587,
                username: data.username || '',
                password: '',
                encryption: data.encryption || 'none',
                from_address: data.from_address || '',
                from_name: data.from_name || '',
                is_active: !!data.is_active
            });
        } else {
            showError(result.message);
        }
        setLoading(false);
    };

    const handleChange = (field) => (event) => {
        const value = field === 'is_active' ? event.target.checked : event.target.value;
        setForm((prev) => ({ ...prev, [field]: value }));
        setErrors((prev) => ({ ...prev, [field]: undefined }));
    };

    const validate = () => {
        const nextErrors = {};
        if (!form.from_address.trim()) {
            nextErrors.from_address = 'A "from" address is required';
        } else if (!/^\S+@\S+\.\S+$/.test(form.from_address.trim())) {
            nextErrors.from_address = 'Enter a valid email address';
        }
        if (form.mailer === 'smtp') {
            if (!form.host.trim()) nextErrors.host = 'SMTP host is required';
            if (!form.port) nextErrors.port = 'SMTP port is required';
        }

        setErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    };

    const handleSave = async () => {
        if (!validate()) return;

        setSaving(true);
        const payload = { ...form };

        // Only send the password when the user actually typed a new one
        if (!payload.password) {
            delete payload.password;
        }

        const result = await emailSettingService.updateSettings(payload);

        if (result.success) {
            showSuccess(result.message || 'Email settings saved');
            setHasPassword(!!result.data?.has_password);
            setForm((prev) => ({ ...prev, password: '' }));
        } else {
            showError(result.message);
            if (result.errors) setErrors(result.errors);
        }
        setSaving(false);
    };

    const handleTest = async () => {
        if (!testEmail.trim()) {
            showError('Enter an email address to send the test to');
            return;
        }

        setTesting(true);
        const result = await emailSettingService.sendTestEmail(testEmail.trim());

        if (result.success) {
            showSuccess(result.message);
        } else {
            showError(result.message);
        }
        setTesting(false);
    };

    if (loading) {
        return (
            <DashboardLayout userType="employee">
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <CircularProgress />
                </Box>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout userType="employee">
            <Box>
                <Typography variant="h5" component="h2" gutterBottom>
                    Email Settings
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Outgoing mail server used to send invoices and payment reminders to customers.
                </Typography>

                {!form.is_active && (
                    <Alert severity="info" sx={{ mb: 3 }}>
                        These settings are inactive. Until you enable them, the application falls back to the mail
                        configuration in the server environment.
                    </Alert>
                )}

                <Card sx={{ mb: 3 }}>
                    <CardContent>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    select
                                    label="Mailer"
                                    fullWidth
                                    value={form.mailer}
                                    onChange={handleChange('mailer')}
                                    disabled={saving}
                                >
                                    {MAILERS.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={form.is_active}
                                            onChange={handleChange('is_active')}
                                            disabled={saving}
                                        />
                                    }
                                    label="Use these settings for outgoing email"
                                    sx={{ mt: 1 }}
                                />
                            </Grid>

                            {form.mailer === 'smtp' && (
                                <>
                                    <Grid item xs={12} sm={8}>
                                        <TextField
                                            label="SMTP Host"
                                            fullWidth
                                            required
                                            value={form.host}
                                            onChange={handleChange('host')}
                                            error={!!errors.host}
                                            helperText={errors.host || 'e.g. smtp.gmail.com'}
                                            disabled={saving}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            label="Port"
                                            type="number"
                                            fullWidth
                                            required
                                            value={form.port}
                                            onChange={handleChange('port')}
                                            error={!!errors.port}
                                            helperText={errors.port || '587 for TLS, 465 for SSL'}
                                            disabled={saving}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="Username"
                                            fullWidth
                                            value={form.username}
                                            onChange={handleChange('username')}
                                            autoComplete="off"
                                            disabled={saving}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="Password"
                                            type="password"
                                            fullWidth
                                            value={form.password}
                                            onChange={handleChange('password')}
                                            autoComplete="new-password"
                                            helperText={
                                                hasPassword
                                                    ? 'A password is saved. Leave blank to keep it.'
                                                    : 'No password saved yet.'
                                            }
                                            disabled={saving}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            select
                                            label="Encryption"
                                            fullWidth
                                            value={form.encryption}
                                            onChange={handleChange('encryption')}
                                            disabled={saving}
                                        >
                                            {ENCRYPTIONS.map((option) => (
                                                <MenuItem key={option.value} value={option.value}>
                                                    {option.label}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>
                                </>
                            )}

                            <Grid item xs={12}>
                                <Divider sx={{ my: 1 }} />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="From Address"
                                    fullWidth
                                    required
                                    value={form.from_address}
                                    onChange={handleChange('from_address')}
                                    error={!!errors.from_address}
                                    helperText={errors.from_address || 'Address customers will see as the sender'}
                                    disabled={saving}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="From Name"
                                    fullWidth
                                    value={form.from_name}
                                    onChange={handleChange('from_name')}
                                    helperText="e.g. SunEx Courier"
                                    disabled={saving}
                                />
                            </Grid>
                        </Grid>

                        <Stack direction="row" justifyContent="flex-end" sx={{ mt: 3 }}>
                            <Button
                                variant="contained"
                                startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                                onClick={handleSave}
                                disabled={saving}
                            >
                                {saving ? 'Saving...' : 'Save Settings'}
                            </Button>
                        </Stack>
                    </CardContent>
                </Card>

                <Card>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            Send a test email
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Uses the settings currently saved above. Save your changes first.
                        </Typography>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                            <TextField
                                label="Send test to"
                                fullWidth
                                value={testEmail}
                                onChange={(e) => setTestEmail(e.target.value)}
                                disabled={testing}
                            />
                            <Button
                                variant="outlined"
                                startIcon={testing ? <CircularProgress size={16} /> : <SendIcon />}
                                onClick={handleTest}
                                disabled={testing}
                                sx={{ whiteSpace: 'nowrap', minWidth: 160 }}
                            >
                                {testing ? 'Sending...' : 'Send Test'}
                            </Button>
                        </Stack>
                    </CardContent>
                </Card>
            </Box>
        </DashboardLayout>
    );
};

export default EmailSettings;
